import { defineStore } from 'pinia';
import { useServiceStore } from '@modules/service/m-service';
import { useContractsStore } from '@/store/modules/contracts/m-contracts';
import SessionCache from '@plugins/cache/index';
import { MANAGEMENT_OFFICE_CODE, VACANT_MANAGER_CODE } from '@/store/modules/service/constants';
import { i18n } from '@/plugins/i18n';

const cache = new SessionCache('sirvase');

export const useSirvaseStore = defineStore('m-sirvase', {
	state: () => ({
		description: '',
		savedPersonalManagers: [],
	}),
	getters: {
		isDisplayingCatAgent: (state) =>
			!state.savedPersonalManagers.some((manager) => manager.type !== MANAGEMENT_OFFICE_CODE),

		isCompanyContract: () => useContractsStore().connectedContract?.natureTypeOwner?.id === 'J',

		getCatAgent() {
			const name = i18n.global.t('SIRVASE.CUSTOMER_SERVICE_CENTER');

			if (this.isCompanyContract) {
				return {
					type: MANAGEMENT_OFFICE_CODE,
					typeDescription: 'CENTRO DE ATENCION AL CLIENTE EMPRESAS',
					isCat: true,
					isCompanyCat: true,
					managerInfo: {
						name,
						phoneNumber: '913 462 000',
						email: 'empresas@cbnk.es',
					},
				};
			}

			return {
				type: MANAGEMENT_OFFICE_CODE,
				typeDescription: 'GESTOR BANCA PRIVADA',
				isCat: true,
				managerInfo: {
					name,
					phoneNumber: '900 107 411 / 913 109 550',
					email: 'contacto@cbnk.es',
				},
			};
		},

		personalManagers: (state) =>
			state.isDisplayingCatAgent
				? [state.getCatAgent]
				: state.savedPersonalManagers.filter((pm) => pm.type !== VACANT_MANAGER_CODE),
	},

	actions: {
		setDescription(value) {
			this.description = value;
		},

		reset() {
			this.description = null;
			this.savedPersonalManagers = [];
			cache.clear();
		},

		get({ paginationKey, requestId } = {}) {
			const queryParams = {};
			let url = '/customer-support/request';
			const method = 'GET';

			if (requestId) {
				url = `${url}/${requestId}`;
			}

			/* istanbul ignore next */
			if (paginationKey) {
				Object.assign(queryParams, { paginationKey });
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => data);
		},

		request({ payload }) {
			const url = '/customer-support/request';
			const method = 'POST';

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
				payload,
			});
		},

		setRequestStatus({ requestId, status }) {
			const url = `/customer-support/request/${requestId}`;
			const method = 'PATCH';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: { status },
				})
				.then(({ data }) => data);
		},

		getTypologies() {
			const url = '/customer-support/typology';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method: 'GET',
						},
					},
				})
				.then(({ data }) => data);
		},

		getPersonalManagers() {
			const url = '/customer-support/current/user/personal-managers';
			const serviceStore = useServiceStore();

			const key = 'sirvase/personal-managers';
			if (cache.has(key)) {
				return Promise.resolve(cache.get(key));
			}

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method: 'GET',
						},
					},
				})
				.then(({ data }) => {
					this.savedPersonalManagers = data.data.data.filter(
						(pm) => pm.type !== VACANT_MANAGER_CODE
					);
					this.savedPersonalManagers.sort((a, b) => {
						const getSortValue = (manager) => {
							if (!manager.type || typeof manager.type !== 'string') return Infinity;
							const parts = manager.type.split('_');
							return parts.length > 1 ? parseInt(parts[1], 10) || Infinity : Infinity;
						};

						return getSortValue(a) - getSortValue(b);
					});
					cache.set(key, this.savedPersonalManagers);
				})
				.catch(() => []);
		},

		ratePersonalManager(payload) {
			const url = '/customer-support/current/user/personal-managers';
			const method = 'POST';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload,
				})
				.then(() => {
					this.savedPersonalManagers.map((pm) => {
						if (pm.id === payload.agentId) {
							pm.managerInfo.evaluation = payload.evaluation;
							pm.managerInfo.comment = payload.comment;
						}
						return pm;
					});
				});
		},
	},
});
